import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  IconButton,
  Chip,
  Stack,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import {
  Email,
  Pinterest,
  Edit,
  Delete,
  WhatsApp,
  Notifications as NotificationsIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { TopBar } from '../layout/TopBar';
import { BottomNavigation } from '../layout/BottomNavigation';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getEvent, deleteEvent, updateEvent, addEventEditor, removeEventEditor, inviteUserToEventByEmail, subscribeToEvent, EventEditor, Event, Task } from '../services/eventsService';
import { notifyEventCreated, notifyEventShared } from '../services/eventNotificationsService';
import { CoEditorEventDialog } from '../components/CoEditorEventDialog';
import { getUserGuests, Guest as GuestType } from '../services/guestsService';
import { searchHostlyUsers, HostlyUser } from '../services/userSearchService';
import { useSubscription } from '../contexts/SubscriptionContext';
import { scrollableSx } from '../utils/scrollableSx';
import { getRecipeById, Recipe as RecipeType } from '../services/recipesService';
import { checkRecipeAllergies, AllergyConflict } from '../services/allergyCheckService';
import { useInventoryContext, InventoryItem } from '../contexts/InventoryContext';
import { EventsDialog } from '../components/eventsDialog';
import { addToGoogleCalendar } from '../services/calendarIntegrationService';
import { sendBulkGuestNotifications } from '../services/notificationsService';
import { useSafeArea } from '../hooks/useSafeArea';

export const EventDetailsPage: React.FC = () => {
  const { theme } = useTheme();
  const { user, hostlyUser } = useAuth();
  const { isPro } = useSubscription();
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const { getItemById } = useInventoryContext();
  const { top } = useSafeArea();
  const location = useLocation();

  const [event, setEvent] = useState<Event | null>(null);
  const [isOwner, setIsOwner] = useState(true);
  const [coEditOpen, setCoEditOpen] = useState(false);
  const [manageEditorsOpen, setManageEditorsOpen] = useState(false);
  const [invitedPeople, setInvitedPeople] = useState<EventEditor[]>([]);
  const [loadingPeople, setLoadingPeople] = useState(false);
  const [editorBusy, setEditorBusy] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteAsEditor, setInviteAsEditor] = useState(false);
  const [invitingByEmail, setInvitingByEmail] = useState(false);

  const canEdit = isOwner || (!!event && !!user?.uid && (event.editorUserIds?.includes(user.uid) ?? false));
  const editorUserIds = event?.editorUserIds || [];

  const reloadEvent = async (): Promise<Event | null> => {
    if (!eventId) return null;
    const fresh = await getEvent(eventId);
    if (fresh) setEvent(fresh);
    return fresh;
  };

  // Resolve os usuários com acesso (invitedUserIds) para exibir no dialog.
  const loadInvitedPeople = async (source?: Event | null) => {
    setLoadingPeople(true);
    const ids = (source ?? event)?.invitedUserIds || [];
    try {
      const { doc: fbDoc, getDoc: fbGetDoc } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');
      const resolved = await Promise.all(ids.map(async (uid) => {
        try {
          const snap = await fbGetDoc(fbDoc(db, 'users', uid));
          const d = snap.data();
          return { uid, name: d?.name || d?.displayName, email: d?.email } as EventEditor;
        } catch { return { uid } as EventEditor; }
      }));
      setInvitedPeople(resolved);
    } finally {
      setLoadingPeople(false);
    }
  };

  const openManageEditors = async () => {
    setManageEditorsOpen(true);
    setInviteEmail('');
    setInviteAsEditor(isPro);
    await loadInvitedPeople();
  };

  const justPickedRef = React.useRef(false);
  useEffect(() => {
    if (justPickedRef.current) { justPickedRef.current = false; return; }
    const term = inviteEmail.trim();
    if (term.length < 3) { setUserResults([]); return; }
    let cancelled = false;
    setSearchingUsers(true);
    const timer = setTimeout(async () => {
      try {
        const found = await searchHostlyUsers(term);
        if (!cancelled) setUserResults(found.filter(u => u.id !== user?.uid));
      } catch {
        if (!cancelled) setUserResults([]);
      } finally {
        if (!cancelled) setSearchingUsers(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); setSearchingUsers(false); };
  }, [inviteEmail, user?.uid]);

  const handleInviteByEmail = async () => {
    if (!event || !eventId) return;
    setInvitingByEmail(true);
    try {
      const result = await inviteUserToEventByEmail(event, inviteEmail, inviteAsEditor && isPro);
      if (!result.ok) {
        const msg =
          result.reason === 'invalid_email' ? 'Email inválido.'
          : result.reason === 'not_found' ? 'Esse email não é de um usuário do Hostly.'
          : result.reason === 'is_owner' ? 'Você já é o dono do evento.'
          : 'Não foi possível convidar.';
        setSnackbar({ open: true, message: msg, severity: 'error' });
        return;
      }
      const fresh = await reloadEvent();
      await loadInvitedPeople(fresh);
      await notifyEventShared(fresh ?? event, eventId, result.user.uid, inviteAsEditor && isPro);
      const who = result.user.name || result.user.email || 'Pessoa';
      setSnackbar({
        open: true,
        message: result.alreadyHadAccess
          ? (result.promotedToEditor ? `${who} agora pode editar` : `${who} já tinha acesso`)
          : (inviteAsEditor ? `${who} convidado(a) como co-editor(a)` : `${who} convidado(a) para ver o evento`),
        severity: 'success',
      });
      setInviteEmail('');
      setInviteAsEditor(false);
    } catch (e: any) {
      setSnackbar({ open: true, message: e?.message || 'Erro ao convidar', severity: 'error' });
    } finally {
      setInvitingByEmail(false);
    }
  };

  const toggleEditor = async (person: EventEditor, makeEditor: boolean) => {
    if (!eventId) return;
    setEditorBusy(person.uid);
    try {
      if (makeEditor) await addEventEditor(eventId, person.uid);
      else await removeEventEditor(eventId, person.uid);
      await reloadEvent();
      setSnackbar({
        open: true,
        message: makeEditor
          ? `${person.name || person.email || 'Convidado'} agora pode editar`
          : `${person.name || person.email || 'Convidado'} não é mais co-editor`,
        severity: 'success',
      });
    } catch (e: any) {
      setSnackbar({ open: true, message: e?.message || 'Erro ao atualizar co-editor', severity: 'error' });
    } finally {
      setEditorBusy(null);
    }
  };
  const [guests, setGuests] = useState<GuestType[]>([]);
  const [recipes, setRecipes] = useState<RecipeType[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info'
  }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [allergyConflicts, setAllergyConflicts] = useState<AllergyConflict[]>([]);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteMethod, setInviteMethod] = useState<'email' | 'whatsapp' | 'notification' | null>(null);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [sendingNotification, setSendingNotification] = useState(false);
  const [selectedGuestsForInvite, setSelectedGuestsForInvite] = useState<string[]>([]);
  const [publishing, setPublishing] = useState(false);
  const [userResults, setUserResults] = useState<HostlyUser[]>([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [showAllInventory, setShowAllInventory] = useState(false);
  const [showAllRecipes, setShowAllRecipes] = useState(false);

  useEffect(() => {
    const loadEventDetails = async () => {
      if (!eventId || !user?.uid) {
        navigate('/events');
        return;
      }

      // Check for deep link state to open invite dialog
      if (location.state && (location.state as any).openInvite) {
        setShowInviteDialog(true);
        // Clear state to prevent reopening on refresh? (React Router handles this usually)
      }

      try {
        setLoading(true);
        const eventData = await getEvent(eventId);

        const owner = !!eventData && eventData.userId === user.uid;
        const invited = !!eventData && (eventData.invitedUserIds?.includes(user.uid) ?? false);
        if (!eventData || (!owner && !invited)) {
          navigate('/events');
          return;
        }

        setIsOwner(owner);
        setEvent(eventData);

        // Sub-listas (convidados/receitas/inventário) vivem no acervo do DONO,
        // que o convidado não pode ler. Carrega só quando o usuário é o dono.
        if (!owner) {
          setLoading(false);
          return;
        }

        // Carregar convidados
        if (eventData.guests && eventData.guests.length > 0) {
          const allGuests = await getUserGuests(user.uid);
          const eventGuests = allGuests.filter((g: GuestType) =>
            g.id && eventData.guests?.includes(g.id)
          );
          setGuests(eventGuests);
        }

        // Carregar receitas
        if (eventData.recipes && eventData.recipes.length > 0) {
          const settled = await Promise.allSettled(
            eventData.recipes.map((recipeId: string) => getRecipeById(user.uid, recipeId))
          );
          const validRecipes = settled
            .map((r) => (r.status === 'fulfilled' ? r.value : null))
            .filter((r): r is RecipeType => r !== null);
          setRecipes(validRecipes);
        }

        // Carregar itens do inventário
        if (eventData.inventoryItems && eventData.inventoryItems.length > 0) {
          const items = eventData.inventoryItems
            .map((itemId: string) => getItemById(itemId))
            .filter((item): item is InventoryItem => item !== undefined);
          setInventoryItems(items);
        }

        // Verificar conflitos de alergia
        if (eventData.recipes && eventData.recipes.length > 0 && eventData.guests && eventData.guests.length > 0) {
          const conflictsPromises = eventData.recipes.map((recipeId: string) =>
            checkRecipeAllergies(user.uid, recipeId, eventData.id)
          );
          const results = await Promise.all(conflictsPromises);
          // Flatten results
          const allConflicts = results.flat();
          setAllergyConflicts(allConflicts);
        } else {
          setAllergyConflicts([]);
        }

      } catch (error) {
        console.error('Erro ao carregar evento:', error);
        navigate('/events');
      } finally {
        setLoading(false);
      }
    };

    loadEventDetails();
  }, [eventId, user?.uid, navigate, getItemById]);

  useEffect(() => {
    if (!eventId || !user?.uid) return;
    const unsubscribe = subscribeToEvent(eventId, (fresh) => {
      if (!fresh) return;
      const owner = fresh.userId === user.uid;
      const invited = fresh.invitedUserIds?.includes(user.uid) ?? false;
      if (!owner && !invited) return;
      setEvent(fresh);
    });
    return () => unsubscribe();
  }, [eventId, user?.uid]);

  const handleDeleteEvent = async () => {
    if (!eventId || !user?.uid) return;

    setDeleteDialog(true);
  };

  const confirmDeleteEvent = async () => {
    if (!eventId || !user?.uid) return;

    try {
      setDeleting(true);
      await deleteEvent(eventId, user.uid);
      setSnackbar({
        open: true,
        message: 'Evento excluído com sucesso!',
        severity: 'success'
      });
      setTimeout(() => {
        navigate('/events');
      }, 1000);
    } catch (error) {
      console.error('Erro ao deletar evento:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao excluir evento. Tente novamente.',
        severity: 'error'
      });
    } finally {
      setDeleting(false);
      setDeleteDialog(false);
    }
  };

  const handleEditEvent = () => {
    setShowEditDialog(true);
  };

  const handleEventUpdated = async () => {
    // Recarregar os dados do evento após edição
    if (!eventId || !user?.uid) return;

    try {
      const eventData = await getEvent(eventId);
      if (eventData && eventData.userId === user.uid) {
        setEvent(eventData);

        // Recarregar convidados (limpa a lista se todos foram removidos)
        if (eventData.guests && eventData.guests.length > 0) {
          const allGuests = await getUserGuests(user.uid);
          const eventGuests = allGuests.filter((g: GuestType) =>
            g.id && eventData.guests?.includes(g.id)
          );
          setGuests(eventGuests);
        } else {
          setGuests([]);
        }

        // Recarregar receitas (limpa a lista se todas foram removidas)
        if (eventData.recipes && eventData.recipes.length > 0) {
          const settled = await Promise.allSettled(
            eventData.recipes.map((recipeId: string) => getRecipeById(user.uid, recipeId))
          );
          const validRecipes = settled
            .map((r) => (r.status === 'fulfilled' ? r.value : null))
            .filter((r): r is RecipeType => r !== null);
          setRecipes(validRecipes);
        } else {
          setRecipes([]);
        }

        // Recarregar itens do inventário (limpa a lista se todos foram removidos)
        if (eventData.inventoryItems && eventData.inventoryItems.length > 0) {
          const items = eventData.inventoryItems
            .map((itemId: string) => getItemById(itemId))
            .filter((item): item is InventoryItem => item !== undefined);
          setInventoryItems(items);
        } else {
          setInventoryItems([]);
        }

        // Recalcular alergias (senão o alerta continua citando receita removida)
        if (eventData.recipes && eventData.recipes.length > 0 && eventData.guests && eventData.guests.length > 0) {
          const results = await Promise.all(
            eventData.recipes.map((recipeId: string) =>
              checkRecipeAllergies(user.uid, recipeId, eventData.id)
            )
          );
          setAllergyConflicts(results.flat());
        } else {
          setAllergyConflicts([]);
        }
      }
    } catch (error) {
      console.error('Erro ao recarregar evento:', error);
    }
  };

  const handleSendInvite = () => {
    if (guests.length === 0) {
      setSnackbar({
        open: true,
        message: 'Adicione convidados ao evento primeiro',
        severity: 'info'
      });
      return;
    }
    setShowInviteDialog(true);
  };

  const handleInviteMethodSelect = (method: 'email' | 'whatsapp' | 'notification') => {
    setInviteMethod(method);
    setSelectedGuestsForInvite([]);

    if (method === 'notification') {
      const eventDate = new Date(event!.date);
      const formattedDate = eventDate.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
      setNotificationMessage(
        `Você foi convidado para ${event!.title}!\n\n` +
        `Data: ${formattedDate}\n` +
        `Hora: ${event!.time}\n` +
        `Local: ${event!.location || 'A definir'}\n\n` +
        `${event!.description ? `${event!.description}\n\n` : ''}` +
        `${event!.outfit ? `O que usar: ${event!.outfit}\n\n` : ''}` +
        `Aguardo você!`
      );
    }
  };

  const handleToggleGuestForInvite = (guestId: string) => {
    setSelectedGuestsForInvite(prev =>
      prev.includes(guestId)
        ? prev.filter(id => id !== guestId)
        : [...prev, guestId]
    );
  };

  const handleSendEmail = () => {
    if (!event || selectedGuestsForInvite.length === 0) return;

    const eventDate = new Date(event.date);
    const formattedDate = eventDate.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });

    const selectedGuestsList = guests.filter(g => selectedGuestsForInvite.includes(g.id || ''));
    const emailAddresses = selectedGuestsList.map(g => g.email).filter(Boolean).join(',');

    const subject = `Convite: ${event.title}`;
    const body = `Olá!\n\nVocê foi convidado para ${event.title}!\n\n` +
      `Data: ${formattedDate}\n` +
      `Hora: ${event.time}\n` +
      `Local: ${event.location || 'A definir'}\n\n` +
      `${event.description ? `Detalhes: ${event.description}\n\n` : ''}` +
      `Aguardo você!`;

    window.location.href = `mailto:${emailAddresses}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    setShowInviteDialog(false);
    setInviteMethod(null);
    setSelectedGuestsForInvite([]);
  };

  const handleSendWhatsApp = () => {
    if (!event || selectedGuestsForInvite.length === 0) return;

    const eventDate = new Date(event.date);
    const formattedDate = eventDate.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });

    const buildMessage = (guestId: string) => {
      const willBring = event.guestItems?.[guestId];
      return `Olá! Você foi convidado para *${event.title}*!\n\n` +
        `Data: ${formattedDate}\n` +
        `Hora: ${event.time}\n` +
        `Local: ${event.location || 'A definir'}\n\n` +
        `${event.description ? `${event.description}\n\n` : ''}` +
        `${event.outfit ? `O que usar: ${event.outfit}\n\n` : ''}` +
        `${willBring ? `Você vai levar: ${willBring}\n\n` : ''}` +
        `Aguardo você!\n\n` +
        `Enviado pelo Hostly App`;
    };

    const selectedGuestsList = guests.filter(g => selectedGuestsForInvite.includes(g.id || ''));

    if (selectedGuestsList.length === 1) {
      const guest = selectedGuestsList[0];
      if (guest.phone) {
        const cleanPhone = guest.phone.replace(/\D/g, '');
        const phone = cleanPhone.startsWith('55') ? cleanPhone : `55${cleanPhone}`;
        const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(buildMessage(guest.id || ''))}`;
        window.open(whatsappUrl, '_blank');
      }
    } else {
      selectedGuestsList.forEach((guest, index) => {
        if (guest.phone) {
          const cleanPhone = guest.phone.replace(/\D/g, '');
          const phone = cleanPhone.startsWith('55') ? cleanPhone : `55${cleanPhone}`;
          const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(buildMessage(guest.id || ''))}`;
          setTimeout(() => {
            window.open(whatsappUrl, '_blank');
          }, index * 500);
        }
      });
    }

    setShowInviteDialog(false);
    setInviteMethod(null);
    setSelectedGuestsForInvite([]);
  };

  const handleConfirmSendNotification = async () => {
    if (!user || !event) return;

    try {
      setSendingNotification(true);

      const guestsWithEmail = guests.filter(g => g.email);

      if (guestsWithEmail.length === 0) {
        setSnackbar({
          open: true,
          message: 'Nenhum convidado possui email cadastrado',
          severity: 'info'
        });
        return;
      }

      const emails = guestsWithEmail.map(g => g.email!);
      const result = await sendBulkGuestNotifications(
        emails,
        user.uid,
        user.displayName || user.email || 'Um anfitrião',
        notificationMessage,
        {
          eventId: event.id,
          eventTitle: event.title,
          date: event.date,
          time: event.time,
          location: event.location,
          description: event.description,
        }
      );

      if (result.sent > 0) {
        setSnackbar({
          open: true,
          message: `${result.sent} notificação(ões) enviada(s)!${result.notHostlyUsers > 0 ? ` ${result.notHostlyUsers} não são usuários do Hostly.` : ''}`,
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Nenhum convidado é usuário do Hostly',
          severity: 'info'
        });
      }
    } catch (error) {
      console.error('Erro ao enviar notificação:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao enviar notificação',
        severity: 'error'
      });
    } finally {
      setSendingNotification(false);
      setInviteMethod(null);
      setShowInviteDialog(false);
      setNotificationMessage('');
    }
  };

  const handleOpenPinterest = () => {
    let pinterestUrl = (hostlyUser as any)?.pinterestBoardUrl;
    if (pinterestUrl) {
      if (!pinterestUrl.startsWith('http')) {
        pinterestUrl = `https://${pinterestUrl}`;
      }
      window.open(pinterestUrl, '_blank');
    } else {
      setSnackbar({
        open: true,
        message: 'Configure seu mural do Pinterest nas configurações',
        severity: 'info'
      });
    }
  };

  const isDraft = event?.title?.includes('(Rascunho)') || false;

  const handlePublishEvent = async () => {
    if (!event || !eventId || !user?.uid) return;

    try {
      setPublishing(true);

      const cleanTitle = event.title.replace(' (Rascunho)', '').replace(' (Rascunho)', '');
      const cleanDescription = (event.description || '')
        .replace('\n\n[Rascunho salvo automaticamente]', '')
        .replace('[Rascunho salvo automaticamente]', '');

      await updateEvent(eventId, {
        title: cleanTitle,
        description: cleanDescription,
      });

      // Send notifications to invited guests
      const publishedEvent = {
        ...event,
        title: cleanTitle,
        description: cleanDescription,
      };
      notifyEventCreated(publishedEvent, eventId).catch(error => {
        console.error('Erro ao enviar notificações de publicação:', error);
      });

      // Update local state
      setEvent(publishedEvent);

      setSnackbar({
        open: true,
        message: 'Evento publicado com sucesso!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erro ao publicar evento:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao publicar evento. Tente novamente.',
        severity: 'error'
      });
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return (
      <>
        <TopBar
          title="Detalhes do Evento"
          showBackButton
          onBackClick={() => navigate(-1)}
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <CircularProgress sx={{ color: theme.colors.primary }} />
        </Box>
      </>
    );
  }

  if (!event) {
    return null;
  }

  const eventDate = new Date(event.date);
  const formattedDay = eventDate.getDate().toString().padStart(2, '0');
  const formattedMonth = eventDate.toLocaleDateString('pt-BR', { month: 'short' }).toUpperCase();

  return (
    <>
      <TopBar
        title={event?.title || 'Detalhes do Evento'}
        showBackButton
        showProfileButton
        onBackClick={() => navigate(-1)}
      />

      {/* Botões de ação flutuantes (só para o dono do evento) */}
      {isOwner && (
        <Box
          sx={{
            position: 'fixed',
            top: `${top + 70}px`,
            right: '20px',
            zIndex: 1100,
            display: 'flex',
            gap: 1,
          }}
        >
          <IconButton
            onClick={handleEditEvent}
            sx={{
              backgroundColor: theme.colors.card,
              color: theme.colors.text.primary,
              '&:hover': {
                backgroundColor: theme.colors.card,
                opacity: 0.8,
              },
            }}
          >
            <Edit />
          </IconButton>
          <IconButton
            onClick={handleDeleteEvent}
            sx={{
              backgroundColor: theme.colors.card,
              color: '#d32f2f',
              '&:hover': {
                backgroundColor: theme.colors.card,
                opacity: 0.8,
              },
            }}
          >
            <Delete />
          </IconButton>
        </Box>
      )}

      {/* Co-editor (não-dono com permissão de edição): editar logística/tarefas */}
      {canEdit && !isOwner && (
        <Box sx={{ position: 'fixed', top: `${top + 70}px`, right: '20px', zIndex: 1100 }}>
          <IconButton
            onClick={() => setCoEditOpen(true)}
            sx={{ backgroundColor: theme.colors.card, color: theme.colors.text.primary, '&:hover': { backgroundColor: theme.colors.card, opacity: 0.8 } }}
          >
            <Edit />
          </IconButton>
        </Box>
      )}

      <Box
        sx={{
          paddingTop: '70px',
          paddingBottom: '120px',
          maxWidth: '800px',
          margin: '0 auto',
          px: 2,
        }}
      >
        {/* Publish Draft Button */}
        {isDraft && (
          <Button
            fullWidth
            variant="contained"
            onClick={handlePublishEvent}
            disabled={publishing}
            sx={{
              backgroundColor: theme.colors.primary,
              color: theme.colors.text.contrast,
              textTransform: 'none',
              fontWeight: 'bold',
              fontSize: '1rem',
              py: 1.5,
              mb: 2,
              borderRadius: theme.radii.medium,
              '&:hover': {
                backgroundColor: theme.colors.primary,
                opacity: 0.9,
              },
              '&:disabled': {
                backgroundColor: theme.colors.primary,
                opacity: 0.5,
              },
            }}
          >
            {publishing ? <CircularProgress size={24} color="inherit" /> : 'Publicar Evento'}
          </Button>
        )}

        {/* Add to Calendar Button */}
        {event && !isDraft && (
          <Button
            fullWidth
            variant="outlined"
            startIcon={<CalendarIcon />}
            onClick={() => addToGoogleCalendar(event)}
            sx={{
              borderColor: theme.colors.primary,
              color: theme.colors.primary,
              borderRadius: theme.radii.medium,
              padding: '10px',
              fontSize: '0.875rem',
              fontWeight: 600,
              textTransform: 'none',
              mb: 2,
              '&:hover': {
                borderColor: theme.colors.primary,
                backgroundColor: `${theme.colors.primary}10`,
              },
            }}
          >
            Adicionar ao Calendário
          </Button>
        )}

        {/* Grid Layout */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            border: `1px solid ${theme.colors.border}`,
            borderRadius: theme.radii.large,
            overflow: 'hidden',
            backgroundColor: theme.colors.card,
            '& > div': {
              borderBottom: `1px solid ${theme.colors.border}`,
              minHeight: '150px',
            },
            '& > div:nth-of-type(odd)': {
              borderRight: { xs: 'none', sm: `1px solid ${theme.colors.border}` },
            },
            '& > div:nth-last-of-type(1)': {
              borderBottom: 'none',
            },
            '& > div:nth-last-of-type(2)': {
              borderBottom: { xs: `1px solid ${theme.colors.border}`, sm: 'none' },
            },
          }}
        >
          {/* Selo colaborativo */}
          {(editorUserIds.length > 0 || (canEdit && !isOwner)) && (
            <Box sx={{ px: 3, pt: 2 }}>
              <Chip
                size="small"
                label={isOwner ? `Colaborativo · ${editorUserIds.length} co-editor${editorUserIds.length === 1 ? '' : 'es'}` : 'Você é co-editor'}
                sx={{ backgroundColor: `${theme.colors.primary}18`, color: theme.colors.primary, fontWeight: 700 }}
              />
            </Box>
          )}

          {/* Data e Hora juntas */}
          <Box sx={{ p: 3 }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2, color: theme.colors.text.primary }}>
              Data e Hora:
            </Typography>
            <Box
              sx={{
                backgroundColor: theme.colors.secondary,
                color: theme.colors.text.onSecondary,
                padding: '16px',
                borderRadius: theme.radii.medium,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box sx={{ textAlign: 'center', borderRight: `2px solid ${theme.colors.text.contrast}`, pr: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: '700', lineHeight: 1 }}>
                  {formattedDay}
                </Typography>
                <Typography variant="body2" sx={{ textTransform: 'uppercase', mt: 0.5 }}>
                  {formattedMonth}
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: '700', lineHeight: 1 }}>
                  {event.time}
                </Typography>
                <Typography variant="body2" sx={{ textTransform: 'uppercase', mt: 0.5 }}>
                  HORÁRIO
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Convidados */}
          <Box sx={{ p: 3 }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 0.5, color: theme.colors.text.primary }}>
              Convidados: ({(event.guestCount || 0) + (event.guests?.length || 0)})
            </Typography>
            {(event.guestCount || 0) > 0 && (
              <Typography variant="caption" sx={{ color: theme.colors.text.secondary, display: 'block', mb: 1.5 }}>
                {event.guestCount} pessoa{(event.guestCount || 0) === 1 ? '' : 's'} + {event.guests?.length || 0} convidado{(event.guests?.length || 0) === 1 ? '' : 's'} cadastrado{(event.guests?.length || 0) === 1 ? '' : 's'}
              </Typography>
            )}
            <Box sx={{ maxHeight: 200, overflowY: 'auto', mt: (event.guestCount || 0) > 0 ? 0 : 1.5 }}>
              {guests.length === 0 ? (
                <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                  Nenhum convidado cadastrado
                </Typography>
              ) : (
                <>
                  <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                    {guests.map((guest: GuestType) => (
                      <Box
                        component="li"
                        key={guest.id}
                        onClick={() => navigate(`/guests/${guest.id}`)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 1.5,
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 0.7,
                          }
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            mr: 1.5,
                            backgroundColor: guest.allergies ? '#ff9800' : theme.colors.secondary,
                          }}
                        >
                          {guest.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: '500',
                              textDecoration: 'underline',
                              color: theme.colors.text.secondary,
                            }}
                          >
                            {guest.name}
                          </Typography>
                          {guest.allergies && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#ff9800',
                                fontWeight: 'bold',
                                display: 'block',
                              }}
                            >
                              Restrição: {guest.allergies}
                            </Typography>
                          )}
                          {event.guestItems?.[guest.id || ''] && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.colors.primary,
                                display: 'block',
                              }}
                            >
                              Vai levar: {event.guestItems[guest.id || '']}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  {guests.some(g => g.allergies) && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                        Atenção - Restrições Alimentares:
                      </Typography>
                      {guests.filter(g => g.allergies).map((guest: GuestType) => (
                        <Typography key={guest.id} variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                          • <strong>{guest.name}</strong>: {guest.allergies}
                        </Typography>
                      ))}
                    </Alert>
                  )}

                  {/* Warning Detalhado de Conflitos (Service) */}
                  {allergyConflicts.length > 0 && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        ⚠️ Risco de Alergia Detectado!
                      </Typography>
                      {allergyConflicts.map((conflict, index) => (
                        <Box key={`${conflict.guestId}-${index}`} sx={{ mt: 1 }}>
                          <Typography variant="body2">
                            <strong>{conflict.guestName}</strong> (Restrição: {conflict.allergens.join(', ')})
                          </Typography>
                          <Typography variant="caption" color="error">
                            Conflito com ingredientes: {conflict.conflictingIngredients.join(', ')}
                          </Typography>
                        </Box>
                      ))}
                    </Alert>
                  )}
                </>
              )}
            </Box>
          </Box>

          {/* Onde será */}
          <Box sx={{ p: 3 }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2, color: theme.colors.text.primary }}>
              Onde será:
            </Typography>
            <Typography variant="body1" sx={{ color: theme.colors.text.secondary, mb: 1 }}>
              {event.location || 'Local não definido'}
            </Typography>
          </Box>

          {/* O que usar */}
          <Box sx={{ p: 3 }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2, color: theme.colors.text.primary }}>
              O que usar (Dress Code):
            </Typography>
            <Typography variant="body1" sx={{ color: theme.colors.text.secondary }}>
              {(event as any).outfit || 'Não definido'}
            </Typography>
          </Box>

          {/* O que separar (para convidados/co-editores, que nao leem o acervo do dono) */}
          {!isOwner && (event.inventorySnapshot?.length || event.recipesSnapshot?.length) ? (
            <Box sx={{ p: 3 }}>
              {!!event.inventorySnapshot?.length && (
                <>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.colors.text.primary, mb: 1 }}>
                    O que separar ({event.inventorySnapshot.length}):
                  </Typography>
                  <Stack spacing={1} sx={{ mb: 2 }}>
                    {event.inventorySnapshot.map((snap) => (
                      <Box
                        key={snap.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 1,
                          border: `1px solid ${theme.colors.border}`,
                          borderRadius: theme.radii.medium,
                        }}
                      >
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            flexShrink: 0,
                            borderRadius: theme.radii.small,
                            backgroundImage: snap.photoUrl ? `url(${snap.photoUrl})` : 'linear-gradient(135deg, #E1C4B5 0%, #D4A574 100%)',
                            backgroundSize: 'contain',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                          }}
                        />
                        <Typography sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>
                          {snap.name}{snap.quantity && snap.quantity > 1 ? ` (${snap.quantity})` : ''}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </>
              )}
              {!!event.recipesSnapshot?.length && (
                <>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.colors.text.primary, mb: 1 }}>
                    Receitas do evento:
                  </Typography>
                  <Stack spacing={1}>
                    {event.recipesSnapshot.map((snap) => (
                      <Typography key={snap.id} sx={{ color: theme.colors.text.primary }}>
                        {snap.title}
                      </Typography>
                    ))}
                  </Stack>
                </>
              )}
            </Box>
          ) : null}

          {event.mesa?.previewUrl && (
            <Box sx={{ p: 3 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.colors.text.primary, mb: 1 }}>
                Mesa virtual:
              </Typography>
              <Box
                component="img"
                src={event.mesa.previewUrl}
                alt={`Mesa ${event.mesa.name}`}
                onClick={() => navigate('/mesa')}
                sx={{
                  width: '100%',
                  borderRadius: theme.radii.medium,
                  border: `1px solid ${theme.colors.border}`,
                  cursor: 'pointer',
                  display: 'block',
                }}
              />
              <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                {event.mesa.name}
              </Typography>
            </Box>
          )}

          {/* Itens do Acervo */}
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.colors.text.primary }}>
                Itens do Acervo:
              </Typography>
              {inventoryItems.length > 3 && (
                <Button
                  size="small"
                  onClick={() => setShowAllInventory(v => !v)}
                  sx={{ color: theme.colors.text.secondary, fontSize: '0.75rem' }}
                >
                  {showAllInventory ? 'VER MENOS' : `VER TUDO (${inventoryItems.length})`}
                </Button>
              )}
            </Box>
            {inventoryItems.length === 0 ? (
              <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                Nenhum item selecionado
              </Typography>
            ) : (
              (showAllInventory ? inventoryItems : inventoryItems.slice(0, 3)).map((item: InventoryItem) => (
                <Chip
                  key={item.id}
                  label={`${item.itemName}${event.inventoryQuantities?.[item.id] ? ` (${event.inventoryQuantities[item.id]})` : ''}`}
                  onClick={() => navigate(`/inventory/${item.id}`)}
                  sx={{
                    backgroundColor: theme.colors.primary,
                    color: theme.colors.text.contrast,
                    borderRadius: theme.radii.small,
                    mb: 1,
                    mr: 1,
                    '&:hover': {
                      backgroundColor: theme.colors.primary,
                      opacity: 0.8,
                    },
                  }}
                />
              ))
            )}
          </Box>

          {/* Itens Compartilhados */}
          {event.sharedInventoryItems && event.sharedInventoryItems.length > 0 && (
            <Box sx={{ px: 3, pb: 3 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.colors.text.primary, mb: 1 }}>
                Itens Compartilhados:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {event.sharedInventoryItems.map((si) => (
                  <Chip
                    key={`${si.ownerId}-${si.itemId}`}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <span>{si.itemName}{si.quantity && si.quantity > 1 ? ` (${si.quantity})` : ''}</span>
                        <Box
                          component="span"
                          sx={{
                            backgroundColor: 'rgba(255,255,255,0.25)',
                            borderRadius: '4px',
                            px: 0.5,
                            fontSize: '0.65rem',
                            lineHeight: 1.4,
                          }}
                        >
                          De: {si.ownerName}
                        </Box>
                      </Box>
                    }
                    sx={{
                      backgroundColor: theme.colors.primary,
                      color: theme.colors.text.contrast,
                      borderRadius: theme.radii.small,
                      height: 'auto',
                      '& .MuiChip-label': {
                        py: 0.5,
                        whiteSpace: 'normal',
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Receitas */}
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.colors.text.primary }}>
                Receitas:
              </Typography>
              {recipes.length > 3 && (
                <Button
                  size="small"
                  onClick={() => setShowAllRecipes(v => !v)}
                  sx={{ color: theme.colors.text.secondary, fontSize: '0.75rem' }}
                >
                  {showAllRecipes ? 'VER MENOS' : `VER TUDO (${recipes.length})`}
                </Button>
              )}
            </Box>
            {recipes.length === 0 ? (
              <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                Nenhuma receita adicionada
              </Typography>
            ) : (
              (showAllRecipes ? recipes : recipes.slice(0, 3)).map((recipe: RecipeType) => (
                <Box
                  key={recipe.id}
                  onClick={() => navigate(`/recipes/${recipe.id}`)}
                  sx={{
                    backgroundColor: theme.colors.primary,
                    borderRadius: theme.radii.small,
                    padding: '10px 15px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 0.8,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {recipe.imageUrl ? (
                      <Avatar
                        src={recipe.imageUrl}
                        variant="rounded"
                        sx={{
                          width: 30,
                          height: 30,
                          '& img': {
                            objectFit: 'cover',
                          }
                        }}
                      />
                    ) : (
                      <Avatar
                        variant="rounded"
                        sx={{
                          width: 30,
                          height: 30,
                          backgroundColor: theme.colors.secondary,
                        }}
                      />
                    )}
                    <Typography variant="body1" sx={{ color: theme.colors.text.contrast, fontWeight: '500' }}>
                      {recipe.title}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    sx={{
                      backgroundColor: theme.colors.card,
                      color: theme.colors.text.primary,
                      borderRadius: theme.radii.small,
                      padding: '2px 10px',
                      minWidth: 'auto',
                      fontWeight: '700',
                      fontSize: '0.7rem',
                      '&:hover': {
                        backgroundColor: theme.colors.card,
                        opacity: 0.8,
                      },
                    }}
                  >
                    VER
                  </Button>
                </Box>
              ))
            )}
          </Box>

          {/* Tarefas */}
          <Box
            sx={{
              p: 3,
              gridRow: { xs: 'auto', sm: 'span 2' },
              borderBottom: { xs: `1px solid ${theme.colors.border}`, sm: `1px solid ${theme.colors.border} !important` },
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1, color: theme.colors.text.primary }}>
              TAREFAS:
            </Typography>
            {!event?.tasks || event.tasks.length === 0 ? (
              <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                Nenhuma tarefa adicionada
              </Typography>
            ) : (
              [...event.tasks]
                .sort((a: Task, b: Task) => {
                  if (a.date && b.date) return a.date.localeCompare(b.date);
                  if (a.date) return -1;
                  if (b.date) return 1;
                  return 0;
                })
                .map((task: Task) => (
                <Box
                  key={task.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    py: 0.5,
                    borderBottom: `1.5px solid ${theme.colors.border}`,
                  }}
                >
                  <Checkbox checked={task.checked} sx={{ p: 0.5, mr: 0.5 }} disabled />
                  <Typography variant="body2" sx={{ color: theme.colors.text.primary, flex: 1 }}>
                    {task.label}
                  </Typography>
                  {task.date && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: theme.colors.primary,
                        backgroundColor: `${theme.colors.primary}14`,
                        borderRadius: theme.radii.small,
                        px: 0.75,
                        py: 0.25,
                        ml: 1,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {new Date(`${task.date}T12:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
                    </Box>
                  )}
                </Box>
              ))
            )}
          </Box>

          {/* Enviar convite (só o dono convida) */}
          {isOwner && (
          <Box
            sx={{
              p: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Button
              variant="contained"
              startIcon={<Email />}
              onClick={handleSendInvite}
              sx={{
                backgroundColor: theme.colors.primary,
                color: theme.colors.text.contrast,
                borderRadius: theme.radii.medium,
                padding: '12px 24px',
                width: '100%',
                height: '80px',
                fontSize: '1rem',
                fontWeight: '500',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: theme.colors.primary,
                  opacity: 0.8,
                  boxShadow: 'none',
                },
              }}
            >
              Enviar convite
              <br />
              para evento
            </Button>
          </Box>
          )}

          {/* Convite de colaboração (só o dono gerencia) */}
          {isOwner && (
            <Box sx={{ px: 3, pb: 2, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={openManageEditors}
                sx={{
                  backgroundColor: theme.colors.secondary,
                  color: theme.colors.text.onSecondary,
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: theme.radii.medium,
                  width: '100%',
                  py: 1.5,
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: theme.colors.secondary, opacity: 0.9, boxShadow: 'none' },
                }}
              >
                Convide alguém para colaborar{(event.editorUserIds?.length || 0) > 0 ? ` (${event.editorUserIds!.length})` : ''}
              </Button>
            </Box>
          )}

          {/* Mural de inspirações */}
          <Box
            sx={{
              p: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderBottom: { xs: `1px solid ${theme.colors.border}`, sm: `1px solid ${theme.colors.border} !important` },
            }}
          >
            <Button
              variant="contained"
              startIcon={<Pinterest />}
              onClick={handleOpenPinterest}
              sx={{
                backgroundColor: theme.colors.primary,
                color: theme.colors.text.contrast,
                borderRadius: theme.radii.medium,
                padding: '12px 24px',
                width: '100%',
                height: '80px',
                fontSize: '1rem',
                fontWeight: '500',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: theme.colors.primary,
                  opacity: 0.8,
                  boxShadow: 'none',
                },
              }}
            >
              Mural de
              <br />
              inspirações
            </Button>
          </Box>

          {/* Observações */}
          <Box
            sx={{
              p: 3,
              gridColumn: { xs: '1', sm: '1 / -1' },
              borderBottom: 'none !important',
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1, color: theme.colors.text.primary }}>
              OBSERVAÇÕES:
            </Typography>
            <Typography variant="body1" sx={{ color: theme.colors.text.primary, whiteSpace: 'pre-wrap' }}>
              {event.description || 'Nenhuma observação adicionada'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Dialog de edição */}
      {event && (
        <EventsDialog
          open={showEditDialog}
          onClose={() => setShowEditDialog(false)}
          eventData={{
            id: event.id,
            title: event.title,
            date: new Date(event.date).toISOString().split('T')[0],
            time: event.time,
            location: event.location,
            description: event.description,
            guests: event.guests,
            recipes: event.recipes,
            inventoryItems: event.inventoryItems,
            inventoryQuantities: event.inventoryQuantities,
            tasks: event.tasks,
            outfit: (event as any).outfit,
            menu: (event as any).menu,
            photos: event.photos,
            guestCount: event.guestCount,
            guestItems: event.guestItems,
            sharedInventoryItems: event.sharedInventoryItems,
          }}
          onEventCreated={handleEventUpdated}
        />
      )}

      {/* Dialog de seleção de método de envio */}
      <Dialog
        open={showInviteDialog && inviteMethod === null}
        onClose={() => setShowInviteDialog(false)}
      >
        <DialogTitle sx={{ color: theme.colors.text.primary }}>
          Enviar Convite
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: theme.colors.text.primary, mb: 2 }}>
            Escolha como deseja enviar o convite para seus convidados:
          </DialogContentText>
          <List>
            <ListItemButton onClick={() => handleInviteMethodSelect('email')}>
              <Email sx={{ mr: 2, color: theme.colors.primary }} />
              <ListItemText
                primary="Email"
                secondary={`${guests.filter(g => g.email).length} convidado(s) com email`}
                sx={{ color: theme.colors.text.primary }}
              />
            </ListItemButton>
            <ListItemButton onClick={() => handleInviteMethodSelect('whatsapp')}>
              <WhatsApp sx={{ mr: 2, color: '#25D366' }} />
              <ListItemText
                primary="WhatsApp"
                secondary={`${guests.filter(g => g.phone).length} convidado(s) com telefone`}
                sx={{ color: theme.colors.text.primary }}
              />
            </ListItemButton>
            <ListItemButton onClick={() => handleInviteMethodSelect('notification')}>
              <NotificationsIcon sx={{ mr: 2, color: '#FF6B6B' }} />
              <ListItemText
                primary="Notificação Hostly"
                secondary="Apenas para usuários do app"
                sx={{ color: theme.colors.text.primary }}
              />
            </ListItemButton>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInviteDialog(false)} sx={{ color: theme.colors.text.secondary }}>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={inviteMethod === 'email'}
        onClose={() => {
          setInviteMethod(null);
          setShowInviteDialog(false);
          setSelectedGuestsForInvite([]);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: theme.colors.text.primary }}>
          Selecionar Convidados - Email
        </DialogTitle>
        <DialogContent>
          <List>
            {guests.filter(g => g.email).map((guest) => (
              <ListItemButton
                key={guest.id}
                onClick={() => handleToggleGuestForInvite(guest.id || '')}
              >
                <Checkbox
                  checked={selectedGuestsForInvite.includes(guest.id || '')}
                  sx={{ mr: 1 }}
                />
                <ListItemText
                  primary={`${guest.name} ${guest.lastName || ''}`}
                  secondary={guest.email}
                  sx={{ color: theme.colors.text.primary }}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setInviteMethod(null);
              setShowInviteDialog(false);
              setSelectedGuestsForInvite([]);
            }}
            sx={{ color: theme.colors.text.secondary }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSendEmail}
            disabled={selectedGuestsForInvite.length === 0}
            variant="contained"
            sx={{ backgroundColor: theme.colors.primary, color: theme.colors.text.contrast }}
          >
            Enviar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={inviteMethod === 'whatsapp'}
        onClose={() => {
          setInviteMethod(null);
          setShowInviteDialog(false);
          setSelectedGuestsForInvite([]);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: theme.colors.text.primary }}>
          Selecionar Convidados - WhatsApp
        </DialogTitle>
        <DialogContent>
          <List>
            {guests.filter(g => g.phone).map((guest) => (
              <ListItemButton
                key={guest.id}
                onClick={() => handleToggleGuestForInvite(guest.id || '')}
              >
                <Checkbox
                  checked={selectedGuestsForInvite.includes(guest.id || '')}
                  sx={{ mr: 1 }}
                />
                <ListItemText
                  primary={`${guest.name} ${guest.lastName || ''}`}
                  secondary={guest.phone}
                  sx={{ color: theme.colors.text.primary }}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setInviteMethod(null);
              setShowInviteDialog(false);
              setSelectedGuestsForInvite([]);
            }}
            sx={{ color: theme.colors.text.secondary }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSendWhatsApp}
            disabled={selectedGuestsForInvite.length === 0}
            variant="contained"
            sx={{ backgroundColor: theme.colors.primary, color: theme.colors.text.contrast }}
          >
            Enviar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de notificação */}
      <Dialog
        open={inviteMethod === 'notification'}
        onClose={() => {
          setInviteMethod(null);
          setShowInviteDialog(false);
        }}
      >
        <DialogTitle sx={{ color: theme.colors.text.primary }}>
          Enviar Notificação
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: theme.colors.text.primary, mb: 2 }}>
            Personalize a mensagem do convite:
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            rows={6}
            value={notificationMessage}
            onChange={(e) => setNotificationMessage(e.target.value)}
            placeholder="Digite sua mensagem..."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setInviteMethod(null);
              setShowInviteDialog(false);
            }}
            sx={{ color: theme.colors.text.secondary }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmSendNotification}
            disabled={sendingNotification || !notificationMessage.trim()}
            variant="contained"
            sx={{ backgroundColor: theme.colors.primary, color: theme.colors.text.contrast }}
          >
            {sendingNotification ? <CircularProgress size={20} color="inherit" /> : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmação de exclusão */}
      <Dialog
        open={deleteDialog}
        onClose={() => !deleting && setDeleteDialog(false)}
      >
        <DialogTitle sx={{ color: theme.colors.text.primary }}>
          Excluir Evento
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: theme.colors.text.primary }}>
            Deseja realmente excluir o evento <strong>{event?.title}</strong>?
          </DialogContentText>
          <DialogContentText sx={{ color: theme.colors.text.secondary, mt: 1 }}>
            Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog(false)}
            disabled={deleting}
            sx={{ color: theme.colors.text.secondary }}
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmDeleteEvent}
            disabled={deleting}
            sx={{
              backgroundColor: '#d32f2f',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#d32f2f',
                opacity: 0.8,
              },
              '&:disabled': {
                backgroundColor: '#d32f2f',
                opacity: 0.5,
              }
            }}
          >
            {deleting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edição do co-editor (logística + tarefas) */}
      {event && (
        <CoEditorEventDialog
          open={coEditOpen}
          event={event}
          onClose={() => setCoEditOpen(false)}
          onSaved={reloadEvent}
        />
      )}

      {/* Compartilhar evento (dono): convida por email e gerencia co-editores */}
      <Dialog open={manageEditorsOpen} onClose={() => setManageEditorsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: theme.colors.text.primary }}>Compartilhar evento</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: theme.colors.text.secondary, mb: 1.5 }}>
            Convide alguém pelo email da conta Hostly. Ela passa a ver o evento no app dela.
          </Typography>

          <Stack spacing={1.25} sx={{ mb: 2 }}>
            <TextField
              label="Nome, @usuário ou email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && inviteEmail.trim() && !invitingByEmail) { e.preventDefault(); handleInviteByEmail(); } }}
              size="small"
              fullWidth
              disabled={invitingByEmail}
              helperText={searchingUsers ? 'Procurando…' : 'Digite 3 letras para buscar'}
            />

            {userResults.length > 0 && (
              <List dense sx={{ maxHeight: 200, ...scrollableSx(theme), border: `1px solid ${theme.colors.border}`, borderRadius: theme.radii.medium, py: 0 }}>
                {userResults.map((u) => (
                  <ListItemButton
                    key={u.id}
                    onClick={() => { justPickedRef.current = true; setInviteEmail(u.email); setUserResults([]); }}
                    sx={{ borderBottom: `1px solid ${theme.colors.border}` }}
                  >
                    <Avatar src={u.profilePictureUrl} sx={{ width: 30, height: 30, mr: 1.5 }}>
                      {(u.name || u.email).charAt(0).toUpperCase()}
                    </Avatar>
                    <ListItemText
                      primary={`${u.name} ${u.lastName || ''}`.trim() || u.email}
                      secondary={u.email}
                      primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
                      secondaryTypographyProps={{ fontSize: '0.72rem' }}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={inviteAsEditor && isPro}
                    onChange={(e) => setInviteAsEditor(e.target.checked)}
                    disabled={invitingByEmail || !isPro}
                    sx={{ p: 0.5 }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: isPro ? theme.colors.text.primary : theme.colors.text.secondary }}>
                    Pode editar (co-organizador){!isPro ? ' — Pro' : ''}
                  </Typography>
                }
              />
              <Button
                variant="contained"
                size="small"
                disabled={invitingByEmail || !inviteEmail.trim()}
                onClick={handleInviteByEmail}
                sx={{ backgroundColor: theme.colors.primary, textTransform: 'none', minWidth: 96 }}
              >
                {invitingByEmail ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Convidar'}
              </Button>
            </Box>
          </Stack>

          <Typography variant="subtitle2" sx={{ color: theme.colors.text.primary, fontWeight: 700, mb: 1 }}>
            Pessoas com acesso
          </Typography>
          <Typography variant="caption" sx={{ color: theme.colors.text.secondary, display: 'block', mb: 1.5 }}>
            Qualquer pessoa pode ser convidada para ver o evento. A co-edição em conjunto é do plano Pro; no Pro, o co-editor também entra com itens do próprio acervo.
          </Typography>
          {loadingPeople ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
          ) : invitedPeople.length === 0 ? (
            <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
              Ninguém com acesso ainda. Convide alguém pelo email acima.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {invitedPeople.map((p) => {
                const isEditor = editorUserIds.includes(p.uid);
                const busy = editorBusy === p.uid;
                return (
                  <Box key={p.uid} sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                    p: 1.5, borderRadius: theme.radii.medium,
                    border: `1px solid ${isEditor ? theme.colors.primary : theme.colors.border}`,
                    backgroundColor: isEditor ? `${theme.colors.primary}10` : 'transparent',
                  }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ color: theme.colors.text.primary, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.name || p.email || p.uid}
                      </Typography>
                      {p.email && <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>{p.email}</Typography>}
                    </Box>
                    <Button
                      size="small"
                      variant={isEditor ? 'outlined' : 'contained'}
                      disabled={busy}
                      onClick={() => toggleEditor(p, !isEditor)}
                      sx={isEditor
                        ? { color: theme.colors.text.secondary, borderColor: theme.colors.border, textTransform: 'none', minWidth: 96 }
                        : { backgroundColor: theme.colors.primary, textTransform: 'none', minWidth: 96 }}
                    >
                      {busy ? <CircularProgress size={16} /> : isEditor ? 'Remover' : 'Co-editor'}
                    </Button>
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageEditorsOpen(false)} sx={{ color: theme.colors.text.secondary }}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <BottomNavigation />
    </>
  );
};
